import { Close, MoveDown, ExpandMore, Error } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import FieldMappingSelector from "../../../components/FieldMappingSelector";
import { hostname, parseError } from "../../../utils";

interface FieldMapping {
  email?: string[];
  phone?: string[];
  linkedinUrl?: string[];
  address?: string[];
  city?: string[];
  state?: string[];
  zip?: string[];
}

interface ListHeader {
  id: string;
  name: string;
  type?: string;
}

interface MoveToSequenceDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  companyId: string;
  listId: string;
  selectedRowIds: readonly string[];
  onSuccess: () => void;
}

interface Sequence {
  id: string;
  name: string;
}

const MoveToSequenceDialog: React.FC<MoveToSequenceDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  selectedRowIds,
  onSuccess,
}) => {
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [removeFromList, setRemoveFromList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSequences, setLoadingSequences] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [listHeaders, setListHeaders] = useState<ListHeader[]>([]);
  const [showFieldMappings, setShowFieldMappings] = useState(false);

  useEffect(() => {
    if (open) {
      loadSequences();
      loadListHeaders();
      setFieldMappings({});
      setShowFieldMappings(false);
    }
  }, [open]);

  const loadListHeaders = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/lists/${listId}`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const listData = response.data?.list || response.data;
      if (listData && listData.headers) {
        // Filter out action type headers
        const headers = listData.headers.filter(
          (h: any) => h.type !== "action"
        );
        setListHeaders(headers);
      }
    } catch (err) {
      console.error("Failed to load list headers:", err);
    }
  };

  const loadSequences = async () => {
    setLoadingSequences(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/sequences`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.sequences) {
        setSequences(response.data.sequences);
      } else if (response.data && response.data.items) {
        // Handle paginated response format
        setSequences(response.data.items);
      }
    } catch (err: any) {
      const errorMessage = parseError(err);
      setError(`Failed to load sequences: ${errorMessage}`);
    } finally {
      setLoadingSequences(false);
    }
  };

  const handleMove = async () => {
    setError(null);

    if (!selectedSequenceId) {
      setError("Please select a sequence");
      return;
    }

    if (selectedRowIds.length === 0) {
      setError("No rows selected");
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();

      // Build request body with optional field mappings
      const requestBody: any = {
        companyId,
        rowIds: selectedRowIds,
        sequenceId: selectedSequenceId,
        removeFromList,
      };

      // Only include fieldMappings if any are configured
      if (
        fieldMappings.email?.length ||
        fieldMappings.phone?.length ||
        fieldMappings.linkedinUrl?.length ||
        fieldMappings.address?.length ||
        fieldMappings.city?.length ||
        fieldMappings.state?.length ||
        fieldMappings.zip?.length
      ) {
        requestBody.fieldMappings = fieldMappings;
      }

      const response = await axios.post(
        `${hostname}/lists/${listId}/move-to-sequence`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { addedCount, failedCount, failedRows, removedFromList } = response.data;

      const selectedSequence = sequences.find(
        (s) => s.id === selectedSequenceId
      );
      const sequenceName = selectedSequence?.name || "sequence";

      // Store results and show results dialog
      setResults({
        addedCount,
        failedCount,
        failedRows: failedRows || [],
        removedFromList,
        sequenceName,
      });
      setShowResults(true);
    } catch (err: any) {
      const errorMessage = parseError(err);
      setError(errorMessage);
      try {
        eventListener({
          type: "SET_ERROR",
          error: `Failed to move rows to sequence: ${errorMessage}`,
        });
      } catch (eventError) {
        console.error("EventListener error:", eventError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedSequenceId("");
      setRemoveFromList(false);
      setError(null);
      setShowResults(false);
      setResults(null);
      onClose();
    }
  };

  const handleResultsClose = () => {
    setShowResults(false);
    setResults(null);
    handleClose();
    onSuccess();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MoveDown color="primary" />
            <Typography variant="h6">Move to Sequence</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Move {selectedRowIds.length} selected row
            {selectedRowIds.length !== 1 ? "s" : ""} to a sequence. Rows must
            have valid email and first name fields to be added successfully.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="sequence-select-label">Select Sequence</InputLabel>
            <Select
              labelId="sequence-select-label"
              value={selectedSequenceId}
              onChange={(e) => setSelectedSequenceId(e.target.value)}
              label="Select Sequence"
              disabled={loading || loadingSequences}
            >
              {sequences.length === 0 && !loadingSequences && (
                <MenuItem value="" disabled>
                  No sequences available
                </MenuItem>
              )}
              {loadingSequences && (
                <MenuItem value="" disabled>
                  Loading sequences...
                </MenuItem>
              )}
              {sequences.map((sequence) => (
                <MenuItem key={sequence.id} value={sequence.id}>
                  {sequence.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={removeFromList}
                onChange={(e) => setRemoveFromList(e.target.checked)}
                disabled={loading}
              />
            }
            label="Remove from current list after moving"
          />

          {/* Advanced Section */}
          {listHeaders.length > 0 && (
            <Accordion
              expanded={showFieldMappings}
              onChange={() => setShowFieldMappings(!showFieldMappings)}
              sx={{ mt: 2, boxShadow: "none", "&:before": { display: "none" } }}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ px: 0, minHeight: "auto", "& .MuiAccordionSummary-content": { my: 1 } }}
              >
                <Typography variant="body2" color="text.secondary">
                  Advanced
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Field Mapping
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Customize which fields are used for contact info and address.
                  Fields are tried in the order you select them.
                </Typography>
                <FieldMappingSelector
                  fieldMappings={fieldMappings}
                  onChange={setFieldMappings}
                  availableHeaders={listHeaders}
                  showHelp={false}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            Note: Only rows with valid email addresses and first names will be
            added to the sequence. Rows that don't meet these requirements will
            be skipped.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <LoadingButton onClick={handleClose} disabled={loading} variant="text">
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleMove}
          loading={loading}
          variant="contained"
          color="primary"
          disabled={!selectedSequenceId || loadingSequences}
        >
          Move to Sequence
        </LoadingButton>
      </DialogActions>
    </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={showResults}
        onClose={handleResultsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Move Results</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleResultsClose}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {results && (
            <Box>
              {/* Success Summary */}
              {results.addedCount > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully added {results.addedCount} row
                  {results.addedCount !== 1 ? "s" : ""} to {results.sequenceName}
                  {results.removedFromList && " and removed from current list"}
                </Alert>
              )}

              {/* Failure Summary */}
              {results.failedCount > 0 && (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to add {results.failedCount} row
                    {results.failedCount !== 1 ? "s" : ""}
                  </Alert>

                  {/* Failed Rows Details */}
                  {results.failedRows.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Failed Rows:
                      </Typography>
                      <List dense>
                        {results.failedRows.map((failedRow: any, index: number) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Error color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Row ${index + 1}`}
                              secondary={failedRow.error}
                              secondaryTypographyProps={{
                                sx: { fontSize: '0.875rem' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              )}

              {/* No rows processed */}
              {results.addedCount === 0 && results.failedCount === 0 && (
                <Alert severity="info">
                  No rows were processed
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={handleResultsClose} variant="contained">
            Close
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MoveToSequenceDialog;
